import React, { useEffect, useMemo, useState } from 'react'
import { X, Check, XCircle, FileDiff, Loader2 } from 'lucide-react'
import { apiClient } from '../services/apiClient'

type DiffLineType = 'equal' | 'insert' | 'delete'

interface DiffLine {
  type: DiffLineType
  text: string
  hunkId?: number
}

interface DiffHunk {
  id: number
  changedOpIndices: number[]
  header: string
  previewLines: DiffLine[]
  accepted: boolean
}

interface ProposalDiffReviewModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  onCommitted?: (mergedMarkdown: string) => void
}

function splitLines(text: string): string[] {
  if (!text) return []
  return text.replace(/\r\n/g, '\n').split('\n')
}

function myersDiffLines(a: string[], b: string[]): DiffLine[] {
  const n = a.length
  const m = b.length
  const max = n + m

  const trace: Array<Map<number, number>> = []
  let v = new Map<number, number>()
  v.set(1, 0)

  for (let d = 0; d <= max; d += 1) {
    const newV = new Map<number, number>()

    for (let k = -d; k <= d; k += 2) {
      const kPlus = v.get(k + 1)
      const kMinus = v.get(k - 1)

      let x: number
      if (k === -d || (k !== d && (kMinus ?? -1) < (kPlus ?? -1))) {
        x = kPlus ?? 0
      } else {
        x = (kMinus ?? 0) + 1
      }

      let y = x - k
      while (x < n && y < m && a[x] === b[y]) {
        x += 1
        y += 1
      }

      newV.set(k, x)
      if (x >= n && y >= m) {
        trace.push(newV)
        return buildEditsFromTrace(a, b, trace)
      }
    }

    trace.push(newV)
    v = newV
  }

  return buildEditsFromTrace(a, b, trace)
}

function buildEditsFromTrace(a: string[], b: string[], trace: Array<Map<number, number>>): DiffLine[] {
  const edits: DiffLine[] = []
  let x = a.length
  let y = b.length

  for (let d = trace.length - 1; d >= 0; d -= 1) {
    const v = trace[d]
    const k = x - y

    let prevK: number
    if (d === 0) {
      prevK = 0
    } else {
      const vPrev = trace[d - 1]
      const kPlus = vPrev.get(k + 1)
      const kMinus = vPrev.get(k - 1)

      if (k === -d || (k !== d && (kMinus ?? -1) < (kPlus ?? -1))) {
        prevK = k + 1
      } else {
        prevK = k - 1
      }
    }

    const vPrev = d === 0 ? new Map<number, number>([[0, 0]]) : trace[d - 1]
    const prevX = vPrev.get(prevK) ?? 0
    const prevY = prevX - prevK

    while (x > prevX && y > prevY) {
      edits.push({ type: 'equal', text: a[x - 1] })
      x -= 1
      y -= 1
    }

    if (d === 0) break

    if (x === prevX) {
      edits.push({ type: 'insert', text: b[prevY] ?? '' })
      y = prevY
      x = prevX
    } else {
      edits.push({ type: 'delete', text: a[prevX] ?? '' })
      x = prevX
      y = prevY
    }
  }

  edits.reverse()

  // Coalesce consecutive equal lines into single-line records (keeps UI simple but still line based)
  return edits
}

function buildHunks(lines: DiffLine[], context = 3): { hunks: DiffHunk[]; annotated: DiffLine[] } {
  const annotated: DiffLine[] = lines.map((l) => ({ ...l }))

  const hunks: DiffHunk[] = []
  let hunkId = 0

  const isChange = (t: DiffLineType) => t === 'insert' || t === 'delete'

  // Identify change indices
  const changeIndices: number[] = []
  for (let i = 0; i < annotated.length; i += 1) {
    if (isChange(annotated[i].type)) changeIndices.push(i)
  }

  if (changeIndices.length === 0) {
    return { hunks: [], annotated }
  }

  // Group changes into hunks if they are close
  let groupStart = changeIndices[0]
  let groupEnd = changeIndices[0]

  const flushGroup = () => {
    const start = Math.max(0, groupStart - context)
    const end = Math.min(annotated.length - 1, groupEnd + context)

    const changedOpIndices: number[] = []
    for (let i = groupStart; i <= groupEnd; i += 1) {
      if (isChange(annotated[i].type)) {
        changedOpIndices.push(i)
        annotated[i].hunkId = hunkId
      }
    }

    const previewLines = annotated.slice(start, end + 1)
    const header = `Lines ${start + 1}-${end + 1}`

    hunks.push({
      id: hunkId,
      changedOpIndices,
      header,
      previewLines,
      accepted: true,
    })

    hunkId += 1
  }

  for (let i = 1; i < changeIndices.length; i += 1) {
    const idx = changeIndices[i]
    if (idx - groupEnd <= context * 2) {
      groupEnd = idx
    } else {
      flushGroup()
      groupStart = idx
      groupEnd = idx
    }
  }

  flushGroup()

  return { hunks, annotated }
}

function computeMergedMarkdown(annotated: DiffLine[], hunkAccepted: Map<number, boolean>): string {
  const out: string[] = []
  for (const line of annotated) {
    if (line.type === 'equal') {
      out.push(line.text)
      continue
    }

    const accepted = line.hunkId !== undefined ? (hunkAccepted.get(line.hunkId) ?? true) : true

    if (line.type === 'delete') {
      if (!accepted) out.push(line.text)
      continue
    }

    if (line.type === 'insert') {
      if (accepted) out.push(line.text)
      continue
    }
  }

  return out.join('\n').trimEnd() + '\n'
}

export function ProposalDiffReviewModal({
  isOpen,
  onClose,
  projectId,
  onCommitted,
}: ProposalDiffReviewModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isCommitting, setIsCommitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [baseVersion, setBaseVersion] = useState<number | null>(null)
  const [proposedVersion, setProposedVersion] = useState<number | null>(null)

  const [baseMarkdown, setBaseMarkdown] = useState<string>('')
  const [proposedMarkdown, setProposedMarkdown] = useState<string>('')

  const [hunks, setHunks] = useState<DiffHunk[]>([])
  const [annotatedDiff, setAnnotatedDiff] = useState<DiffLine[]>([])

  const hunkAccepted = useMemo(() => {
    const map = new Map<number, boolean>()
    for (const h of hunks) map.set(h.id, h.accepted)
    return map
  }, [hunks])

  const mergedMarkdown = useMemo(() => {
    return computeMergedMarkdown(annotatedDiff, hunkAccepted)
  }, [annotatedDiff, hunkAccepted])

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const proposalsResponse = await apiClient.get<{ project_id: string; proposals: Array<{ version: number }> }>(
          `/api/projects/${encodeURIComponent(projectId)}/proposals`
        )

        const versions = (proposalsResponse.proposals || []).map((p) => p.version).sort((a, b) => b - a)
        if (versions.length < 2) {
          setError('Not enough proposal history to compare (need at least 2 versions).')
          setBaseVersion(null)
          setProposedVersion(null)
          setBaseMarkdown('')
          setProposedMarkdown('')
          setHunks([])
          setAnnotatedDiff([])
          return
        }

        const proposed = versions[0]
        const base = versions[1]
        setBaseVersion(base)
        setProposedVersion(proposed)

        const [baseMd, proposedMd] = await Promise.all([
          apiClient.get<{ markdown_content: string }>(
            `/api/projects/${encodeURIComponent(projectId)}/proposals/${base}/markdown`
          ),
          apiClient.get<{ markdown_content: string }>(
            `/api/projects/${encodeURIComponent(projectId)}/proposals/${proposed}/markdown`
          ),
        ])

        setBaseMarkdown(baseMd.markdown_content || '')
        setProposedMarkdown(proposedMd.markdown_content || '')

        const diffLines = myersDiffLines(splitLines(baseMd.markdown_content || ''), splitLines(proposedMd.markdown_content || ''))
        const { hunks: builtHunks, annotated } = buildHunks(diffLines, 3)

        setHunks(builtHunks)
        setAnnotatedDiff(annotated)
      } catch (e: any) {
        setError(e.message || 'Failed to load proposal versions')
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [isOpen, projectId])

  const sizeSummary = useMemo(() => {
    if (!baseMarkdown || !proposedMarkdown) return null
    return { baseChars: baseMarkdown.length, proposedChars: proposedMarkdown.length }
  }, [baseMarkdown, proposedMarkdown])

  if (!isOpen) return null

  const acceptAll = () => {
    setHunks((prev) => prev.map((h) => ({ ...h, accepted: true })))
  }

  const rejectAll = () => {
    setHunks((prev) => prev.map((h) => ({ ...h, accepted: false })))
  }

  const toggleHunk = (id: number, accepted: boolean) => {
    setHunks((prev) => prev.map((h) => (h.id === id ? { ...h, accepted } : h)))
  }

  const commit = async () => {
    if (baseVersion == null || proposedVersion == null) return

    setIsCommitting(true)
    setError(null)
    try {
      await apiClient.post<{ project_id: string; new_version: number }>(
        `/api/projects/${encodeURIComponent(projectId)}/proposals/commit`,
        {
          base_version: baseVersion,
          proposed_version: proposedVersion,
          merged_markdown: mergedMarkdown,
        }
      )

      onCommitted?.(mergedMarkdown)
      onClose()
    } catch (e: any) {
      setError(e.message || 'Failed to commit proposal')
    } finally {
      setIsCommitting(false)
    }
  }

  const diffSummary = (() => {
    const adds = annotatedDiff.filter((l) => l.type === 'insert').length
    const dels = annotatedDiff.filter((l) => l.type === 'delete').length
    return { adds, dels }
  })()

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.65)' }}
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    >
      <div
        className="w-full max-w-6xl max-h-[90vh] flex flex-col rounded-lg shadow-2xl"
        style={{ background: 'var(--warm-white)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--sand)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md" style={{ background: 'rgba(200, 88, 53, 0.1)' }}>
              <FileDiff className="w-5 h-5" style={{ color: 'var(--terracotta)' }} />
            </div>
            <div>
              <h2
                className="text-xl font-semibold"
                style={{ fontFamily: 'Crimson Text, Georgia, serif', color: 'var(--charcoal)' }}
              >
                Review Proposal Changes
              </h2>
              <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                {baseVersion != null && proposedVersion != null
                  ? `Comparing v${baseVersion} → v${proposedVersion} • +${diffSummary.adds} -${diffSummary.dels}${sizeSummary ? ` • ${sizeSummary.baseChars}→${sizeSummary.proposedChars} chars` : ''}`
                  : 'Loading proposal history'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={acceptAll}
              className="px-3 py-2 rounded-md text-sm font-medium"
              style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#166534' }}
              disabled={hunks.length === 0 || isLoading || isCommitting}
              title="Accept all changes"
            >
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="px-3 py-2 rounded-md text-sm font-medium"
              style={{ background: 'rgba(185, 28, 28, 0.12)', color: '#7F1D1D' }}
              disabled={hunks.length === 0 || isLoading || isCommitting}
              title="Reject all changes"
            >
              Reject All
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md transition-colors"
              style={{ background: 'transparent', color: 'var(--charcoal-light)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(200, 88, 53, 0.1)'
                e.currentTarget.style.color = 'var(--terracotta)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'var(--charcoal-light)'
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 p-3 rounded-md" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
              <p className="text-sm" style={{ color: '#991B1B' }}>{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--charcoal-light)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading diff…
            </div>
          ) : hunks.length === 0 ? (
            <div className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
              No changes found between the latest versions.
            </div>
          ) : (
            <div className="space-y-4">
              {hunks.map((hunk) => (
                <div key={hunk.id} className="rounded-md border" style={{ borderColor: 'var(--sand)' }}>
                  <div
                    className="flex items-center justify-between px-3 py-2 border-b"
                    style={{ borderColor: 'var(--sand)', background: 'var(--cream)' }}
                  >
                    <div className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
                      Hunk {hunk.id + 1} • {hunk.header}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleHunk(hunk.id, true)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                        style={{
                          background: hunk.accepted ? 'rgba(34, 197, 94, 0.15)' : 'transparent',
                          color: hunk.accepted ? '#166534' : 'var(--charcoal-light)',
                          border: '1px solid var(--sand)',
                        }}
                        disabled={isCommitting}
                      >
                        <Check className="w-3 h-3" />
                        Accept
                      </button>
                      <button
                        onClick={() => toggleHunk(hunk.id, false)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                        style={{
                          background: !hunk.accepted ? 'rgba(185, 28, 28, 0.12)' : 'transparent',
                          color: !hunk.accepted ? '#7F1D1D' : 'var(--charcoal-light)',
                          border: '1px solid var(--sand)',
                        }}
                        disabled={isCommitting}
                      >
                        <XCircle className="w-3 h-3" />
                        Reject
                      </button>
                    </div>
                  </div>

                  <div className="font-mono text-xs overflow-x-auto">
                    {hunk.previewLines.map((line, idx) => {
                      const prefix = line.type === 'insert' ? '+' : line.type === 'delete' ? '-' : ' '
                      const bg =
                        line.type === 'insert'
                          ? 'rgba(34, 197, 94, 0.12)'
                          : line.type === 'delete'
                          ? 'rgba(185, 28, 28, 0.10)'
                          : 'transparent'

                      return (
                        <div
                          key={`${hunk.id}_${idx}`}
                          className="px-3 py-1 whitespace-pre"
                          style={{ background: bg, color: 'var(--charcoal)' }}
                        >
                          <span style={{ color: 'var(--charcoal-light)' }}>{prefix}</span>
                          {' '}
                          {line.text}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--sand)', background: 'var(--cream)' }}>
          <div className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
            {hunks.length > 0
              ? `${hunks.filter((h) => h.accepted).length}/${hunks.length} hunks accepted`
              : ''}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={commit}
              className="px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2"
              style={{ background: 'var(--terracotta)', color: 'white', fontFamily: 'DM Sans, sans-serif' }}
              disabled={isLoading || isCommitting || hunks.length === 0}
            >
              {isCommitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying…
                </>
              ) : (
                'Apply Accepted Changes'
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md font-medium"
              style={{ background: 'transparent', border: '1px solid var(--sand)', color: 'var(--charcoal-light)' }}
              disabled={isCommitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
